// src/app/pages/app/MarketsPage.jsx
import React, { useMemo } from "react";
import { useMarket } from "../../features/markets/marketContext.jsx";
import { ASSET_CLASSES } from "../../features/markets/marketCatalog.js";
import { useMarketPrices } from "../../features/markets/useMarketData.js";
import { 
  Coins, 
  Globe, 
  Gem, 
  ArrowRight, 
  Star, 
  TrendingUp, 
  TrendingDown,
  Sparkles,
  BarChart3,
  Zap,
  Crown,
  Activity
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function fmtPrice(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const decimals = x >= 1000 ? 2 : x >= 1 ? 4 : 6;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(x);
}

function fmtPct(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return `${x > 0 ? '+' : ''}${x.toFixed(2)}%`;
}

const TABS = [
  { key: ASSET_CLASSES.CRYPTO, label: "Crypto", icon: Coins, gradient: "from-amber-400 to-orange-500", bg: "from-amber-500/20 to-orange-500/10" },
  { key: ASSET_CLASSES.FX, label: "FX", icon: Globe, gradient: "from-sky-400 to-blue-500", bg: "from-sky-500/20 to-blue-500/10" },
  { key: ASSET_CLASSES.METALS, label: "Metals", icon: Gem, gradient: "from-yellow-400 to-amber-500", bg: "from-yellow-500/20 to-amber-500/10" },
];

function Tab({ active, onClick, icon: Icon, label, gradient, bg }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex items-center gap-2 px-5 py-3 rounded-xl border text-sm font-semibold transition-all duration-300",
        active
          ? `border-${gradient.split(' ')[1].split('-')[1] || 'indigo'}-400/40 bg-gradient-to-br ${bg} text-white shadow-xl shadow-${gradient.split(' ')[1].split('-')[1] || 'indigo'}-500/20`
          : "border-white/5 bg-white/5 text-gray-400 hover:border-white/20 hover:text-white hover:bg-white/10",
      ].join(" ")}
    >
      <Icon size={18} className={active ? `text-${gradient.split(' ')[1].split('-')[1] || 'indigo'}-400` : ""} />
      {label}
    </button>
  );
}

function Row({ market, live, selected, onSelect, onGoTrade }) {
  const price = live?.price;
  const pct = live?.changePercent;

  const pctColor =
    Number(pct) > 0
      ? "text-emerald-400"
      : Number(pct) < 0
      ? "text-rose-400"
      : "text-gray-400";

  const pctIcon =
    Number(pct) > 0
      ? <TrendingUp size={14} className="text-emerald-400" />
      : Number(pct) < 0
      ? <TrendingDown size={14} className="text-rose-400" />
      : null;

  return (
    <div className={`group px-5 py-4 border-b border-white/5 hover:bg-white/5 transition-all duration-300 ${
      selected ? 'bg-gradient-to-r from-indigo-500/10 to-purple-500/10' : ''
    }`}>
      <div className="flex items-center justify-between gap-4">
        <button 
          type="button" 
          onClick={onSelect} 
          className="text-left flex items-center gap-4 min-w-0 flex-1"
        >
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 transition-all duration-300 ${
            selected 
              ? 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/30' 
              : 'bg-gradient-to-br from-white/10 to-white/5 group-hover:from-indigo-500/30 group-hover:to-purple-500/20'
          }`}>
            {market.icon}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-white font-semibold truncate">
                {market.base}/{market.quote}
              </div>
              {selected && (
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/30 animate-pulse">
                  ● Live
                </span>
              )}
            </div>
            <div className="text-xs text-white/40 truncate">{market.symbol || market.id}</div>
          </div>
        </button>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-white font-bold tabular-nums text-lg">{fmtPrice(price)}</div>
            <div className={`text-xs font-semibold ${pctColor} tabular-nums flex items-center justify-end gap-1`}>
              {pctIcon}
              {fmtPct(pct)}
            </div>
          </div>

          <button
            type="button"
            className="h-9 w-9 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 text-white/30 hover:text-yellow-400 transition-all duration-300 grid place-items-center group-hover:border-white/10"
            title="Add to watchlist"
          >
            <Star size={16} />
          </button>

          <button
            type="button"
            onClick={onGoTrade}
            className="h-9 w-9 rounded-xl border border-white/5 bg-white/5 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-500 hover:border-transparent text-white/30 hover:text-white transition-all duration-300 grid place-items-center group-hover:border-white/10"
            title="Trade"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MarketsPage() {
  const nav = useNavigate();
  const { assetClass, setAssetClass, markets, marketId, setMarketId } = useMarket();

  const list = useMemo(() => (Array.isArray(markets) ? markets : []), [markets]);

  const { prices, status } = useMarketPrices(list, { refreshMs: 15000 });

  const stats = useMemo(() => {
    const total = list.length;
    const active = list.filter(m => {
      const sym = String(m.symbol || m.id || "").toUpperCase();
      return prices[sym]?.price !== undefined;
    }).length;
    const gainers = list.filter(m => {
      const sym = String(m.symbol || m.id || "").toUpperCase();
      return Number(prices[sym]?.changePercent || 0) > 0;
    }).length;
    const losers = list.filter(m => {
      const sym = String(m.symbol || m.id || "").toUpperCase();
      return Number(prices[sym]?.changePercent || 0) < 0;
    }).length;
    return { total, active, gainers, losers };
  }, [list, prices]);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-[#0a0a1a] via-[#0f0f2a] to-[#1a0a2e]">
      {/* Decorative background elements */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-gradient-to-bl from-indigo-600/10 to-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-blue-600/5 to-cyan-600/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/30">
              <Crown size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                Markets
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/20">
                  Live
                </span>
              </h1>
              <p className="text-white/50 text-sm">Real-time market data across all asset classes</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400 font-medium">Live Data</span>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-xs text-white/40">{stats.active} of {stats.total} active</div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Premium Glass Effect */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Markets", value: stats.total, icon: BarChart3, gradient: "from-blue-500 to-cyan-400", bg: "from-blue-500/20 to-cyan-500/10" },
            { label: "Active", value: stats.active, icon: Zap, gradient: "from-emerald-500 to-green-400", bg: "from-emerald-500/20 to-green-500/10" },
            { label: "Gainers", value: stats.gainers, icon: TrendingUp, gradient: "from-emerald-500 to-teal-400", bg: "from-emerald-500/20 to-teal-500/10" },
            { label: "Losers", value: stats.losers, icon: TrendingDown, gradient: "from-rose-500 to-red-400", bg: "from-rose-500/20 to-red-500/10" },
          ].map((stat) => (
            <div key={stat.label} className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 hover:border-white/20 transition-all duration-300 hover:scale-[1.02]">
              <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${stat.gradient} opacity-5 rounded-full blur-2xl`} />
              <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${stat.bg} flex items-center justify-center mb-2`}>
                <stat.icon size={16} className={`bg-gradient-to-br ${stat.gradient} bg-clip-text text-transparent`} />
              </div>
              <div className="text-xs text-white/40">{stat.label}</div>
              <div className={`text-lg font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs - Premium Glass Effect */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TABS.map((t) => (
            <Tab
              key={t.key}
              label={t.label}
              icon={t.icon}
              gradient={t.gradient}
              bg={t.bg}
              active={assetClass === t.key}
              onClick={() => setAssetClass(t.key)}
            />
          ))}
        </div>

        {/* Markets Table - Premium Glass Effect */}
        <div className="relative rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 shadow-2xl">
          {/* Decorative glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl" />

          <div className="relative">
            <div className="px-5 py-4 border-b border-white/5 bg-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-white font-semibold flex items-center gap-2">
                  <Activity size={16} className="text-indigo-400" />
                  All Instruments
                  <span className="text-xs text-white/30 font-normal ml-2">({list.length})</span>
                </div>
                <div className="text-sm text-white/30">Real-time pricing with live updates</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/30">
                {!status.ok && (
                  <span className="text-amber-400/70">⚠️ Reconnecting...</span>
                )}
              </div>
            </div>

            {list.length === 0 ? (
              <div className="p-16 text-center">
                <div className="text-6xl mb-4 opacity-50">📊</div>
                <div className="text-white/50 font-medium">No markets available</div>
                <div className="text-sm text-white/30 mt-1">Check back later for new listings</div>
              </div>
            ) : (
              <div>
                {list.map((m) => {
                  const sym = String(m.symbol || m.id || "").toUpperCase();
                  const live = prices[sym];
                  const selected = marketId === m.id;

                  return (
                    <Row
                      key={m.id}
                      market={m}
                      live={live}
                      selected={selected}
                      onSelect={() => setMarketId(m.id)}
                      onGoTrade={() => {
                        setMarketId(m.id);
                        nav("/app/trade");
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center">
          <span className="flex items-center justify-center gap-2 text-xs text-white/20">
            <Sparkles size={12} className="text-indigo-400/50" />
            All prices are sourced from leading exchanges and updated in real-time
          </span>
        </div>
      </div>
    </div>
  );
}
